import { Box, Button, Card, Typography } from "@mui/material";
import ModelApplicationBox from "./ModelApplicationBox.jsx";
import modelTasks from "../../constants/modelTasks.json";
import modelFrameworks from "../../constants/modelFrameworks.json";
import { useEdition } from "../../edition/EditionContext";
import useCurrentUser from "../../hooks/useCurrentUser";

const ModelCard = ({ model, expanded, onToggleExpand, onTest }) => {
  const downloadUrl = model.pt || model.onnx;
  const isDownloadAvailable = Boolean(downloadUrl);
  const taskInfo = modelTasks.find(
    (task) => task.value === model.info.modelTask,
  );
  const frameworkInfo = modelFrameworks.find(
    (framework) => framework.value === model.info.modelFramework,
  );
  const metrics = model.info?.metrics;
  const hasMetrics = metrics && (metrics.inferenceTime || metrics.mAP);
  const imgSrc =
    model.test_images?.[0] ||
    (taskInfo?.image_src ? `/${taskInfo.image_src}` : "/default_image.png");
  const { isDemo } = useEdition();
  const { permissions } = useCurrentUser();
  return (
    <Card
      elevation={0}
      sx={{
        border: 2,
        borderColor: "rgb(243, 243, 243)",
        p: 2,
        borderRadius: 6,
      }}
    >
      <Box sx={{ display: "flex", justifyContent: "space-between" }}>
        {/* Left side (text and metadata) */}
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 1,
            width: "70%",
          }}
        >
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
            <Typography variant="h6" fontWeight="medium">
              {model.name}
            </Typography>
            {model.info?.application && (
              <ModelApplicationBox
                sx={{ ml: "auto" }}
                type={model.info.application}
              >
                {model.info.application}
              </ModelApplicationBox>
            )}
          </Box>

          {/* Metrics */}
          {(hasMetrics ||
            model.info?.modelFramework ||
            model.info?.modelTask) && (
            <Box
              sx={{
                alignItems: "center",
                display: "flex",
                flexWrap: "wrap",
                gap: 1,
                width: "100%",
              }}
            >
              {hasMetrics ? (
                <Typography variant="body2" color="text.secondary">
                  {metrics.inferenceTime
                    ? `Speed: ${metrics.inferenceTime}`
                    : ""}
                  {metrics.inferenceTime && metrics.mAP ? " | " : ""}
                  {metrics.mAP ? `mAP: ${metrics.mAP}` : ""}
                </Typography>
              ) : null}
              {hasMetrics &&
                (model.info?.modelFramework || model.info?.modelTask) && (
                  <Typography variant="caption" color="text.secondary">
                    |
                  </Typography>
                )}
              {model.info?.modelFramework && (
                <Typography
                  variant="caption"
                  fontWeight={700}
                  sx={{
                    color: frameworkInfo?.color || "#7180a0",
                    textTransform: "uppercase",
                  }}
                >
                  {model.info.modelFramework}
                </Typography>
              )}
              {model.info?.modelFramework && model.info?.modelTask && (
                <Typography variant="caption" color="text.secondary">
                  |
                </Typography>
              )}
              {model.info?.modelTask && (
                <Typography
                  variant="caption"
                  fontWeight={700}
                  sx={{
                    color: taskInfo?.color || "#7180a0",
                    textTransform: "uppercase",
                  }}
                >
                  {model.info.modelTask}
                </Typography>
              )}
            </Box>
          )}

          {/* Description */}
          <Box>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{
                whiteSpace: "pre-wrap",
                wordBreak: "break-word",
                overflowWrap: "break-word",
              }}
            >
              {!model.info?.description
                ? "No description available."
                : expanded
                  ? model.info.description
                  : model.info.description.slice(0, 320) +
                    (model.info.description.length > 320 ? "..." : "")}
            </Typography>

            {model.info?.description && model.info.description.length > 320 && (
              <Button
                onClick={onToggleExpand}
                sx={{
                  mt: 0.5,
                  pl: 0,
                  pr: 0,
                  minWidth: 0,
                  textTransform: "none",
                  fontSize: "12px",
                  color: "black",
                  background: "none",
                  boxShadow: "none",
                  alignSelf: "flex-start",
                }}
              >
                {expanded ? "Show Less" : "Show More"}
              </Button>
            )}
          </Box>

          {/* Action buttons */}
          <Box sx={{ display: "flex", gap: 1 }}>
            {permissions.includes("download_model") && (
              <Button
                variant="outlined"
                component="a"
                href={isDownloadAvailable ? downloadUrl : undefined}
                download={
                  isDownloadAvailable
                    ? `${model.name.replace(/\s+/g, "_")}.${
                        downloadUrl.endsWith(".onnx") ? "onnx" : "pt"
                      }`
                    : undefined
                }
                disabled={!isDownloadAvailable || isDemo}
                sx={{
                  mt: 2,
                  ml: 2.5,
                  textTransform: "none",
                  borderRadius: 4,
                  boxShadow: "none",
                  alignSelf: "flex-start",
                  borderRadius: "12px",
                  // width: '120px',
                  color: "#16a4b0",
                  borderColor: "#16a4b0",
                  // '&:hover': { backgroundColor: '#f0f0f0', borderColor: '#16a4b0' }
                  opacity: isDownloadAvailable ? 1 : 0.5,
                  cursor: isDownloadAvailable ? "pointer" : "not-allowed",
                }}
              >
                Download Model
              </Button>
            )}
            <Button
              variant="outlined"
              onClick={onTest}
              sx={{
                mt: 2,
                ml: 2.5,
                textTransform: "none",
                borderRadius: 4,
                boxShadow: "none",
                alignSelf: "flex-start",
                borderRadius: "12px",
                width: "120px",
                color: "#16a4b0",
                borderColor: "#16a4b0",
                // '&:hover': { backgroundColor: '#f0f0f0', borderColor: '#16a4b0' }
              }}
            >
              Test Model
            </Button>
          </Box>
        </Box>

        {/* Right side (image) */}
        <Box
          component="img"
          src={imgSrc}
          alt={model.name}
          sx={{
            width: 320,
            height: 210,
            borderRadius: 2,
            objectFit: "cover",
            m: 0.7,
          }}
        />
      </Box>
    </Card>
  );
};

export default ModelCard;
